import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import NotificationBell from "@/components/marketplace/NotificationBell";
import Logo from "@/components/marketplace/Logo";
import { roleHome } from "@/lib/roles";
import { LogOut, User, Menu, Sun, Moon } from "lucide-react";

const NavItem = ({ to, children, testid, onClick }) => (
  <NavLink
    to={to}
    data-testid={testid}
    onClick={onClick}
    className={({ isActive }) =>
      `text-sm tracking-wide transition-colors duration-300 ${
        isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
      }`
    }
  >
    {children}
  </NavLink>
);

export default function Nav() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const home = roleHome(user);

  const close = () => setOpen(false);

  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-background/80 border-b hairline">
      <div className="max-w-[1400px] mx-auto flex items-center justify-between gap-3 px-4 md:px-8 py-4 md:py-5">
        <Link to={home} data-testid="nav-logo" className="flex items-center gap-2 md:gap-3 shrink-0">
          <Logo className="h-7 w-7 md:h-9 md:w-9" />
          <span className="font-serif text-xl md:text-2xl tracking-wide whitespace-nowrap">GetJewelDesigns</span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <NavItem to="/categories" testid="nav-categories">Categories</NavItem>
          <NavItem to="/how-it-works" testid="nav-how">How it works</NavItem>
          <NavItem to="/blog" testid="nav-blog">Journal</NavItem>
          <NavItem to="/freelancer/apply" testid="nav-freelancer-apply">Design with us</NavItem>
        </nav>

        <div className="flex items-center gap-2 md:gap-4">
          {/* Theme lives in the drawer on mobile — five unlabelled icons in a row
              left no room for 44px targets and put sign-out beside the menu. */}
          <button
            data-testid="nav-theme-toggle"
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            onClick={toggleTheme}
            className="hidden md:inline-flex items-center justify-center min-h-11 min-w-11 text-muted-foreground hover:text-foreground transition-colors duration-300"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" strokeWidth={1} /> : <Moon className="h-4 w-4" strokeWidth={1} />}
          </button>
          {user === null ? null /* auth still resolving — avoid flashing "Sign in" at logged-in users */ : user && user !== false ? (
            <>
              <NotificationBell />
              <Link
                to={home}
                data-testid="nav-dashboard"
                aria-label={`${user.name} — go to my dashboard`}
                className="hidden md:flex text-sm text-muted-foreground hover:text-foreground transition-colors duration-300 items-center gap-2 min-h-11"
              >
                <User className="h-4 w-4" strokeWidth={1} />
                <span className="max-w-[120px] truncate">{user.name}</span>
              </Link>
              <Button
                data-testid="nav-logout"
                variant="ghost"
                size="sm"
                className="hidden md:inline-flex text-xs uppercase tracking-widest min-h-11"
                onClick={async () => { await logout(); nav("/"); }}
              >
                <LogOut className="h-4 w-4 mr-2" strokeWidth={1} />
                <span>Sign out</span>
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="hidden sm:inline text-sm text-muted-foreground hover:text-foreground transition-colors duration-300 whitespace-nowrap">Sign in</Link>
              <Link
                to="/register"
                data-testid="nav-register"
                className="text-[10px] md:text-xs uppercase tracking-widest border border-primary text-primary px-3 md:px-5 py-2 hover:bg-primary hover:text-primary-foreground transition-colors duration-300 whitespace-nowrap"
              >
                Begin
              </Link>
            </>
          )}

          {/* Mobile hamburger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="nav-menu-toggle"
                aria-label="Open menu"
                className="md:hidden inline-flex items-center justify-center border hairline min-h-11 min-w-11 hover:border-primary/60 transition-colors duration-300"
              >
                <Menu className="h-4 w-4" strokeWidth={1} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-background border-l hairline rounded-none w-[85vw] max-w-sm p-6 overflow-y-auto">
              <div className="flex items-center gap-2 mb-8">
                <Logo className="h-8 w-8" />
                <span className="font-serif text-2xl">GetJewelDesigns</span>
              </div>

              {/* Signed-in business comes first; the marketing links are secondary
                  once you have an account. */}
              {user && user !== false && (
                <div className="mb-6 pb-6 border-b hairline">
                  <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">Signed in as</div>
                  <div className="text-base mb-4 truncate">{user.name}</div>
                  <NavItem to={home} testid="mnav-dashboard" onClick={close}>My dashboard</NavItem>
                </div>
              )}

              <nav className="flex flex-col gap-6 text-lg">
                <NavItem to="/categories" testid="mnav-categories" onClick={close}>Categories</NavItem>
                <NavItem to="/how-it-works" testid="mnav-how" onClick={close}>How it works</NavItem>
                <NavItem to="/blog" testid="mnav-blog" onClick={close}>Journal</NavItem>
                <NavItem to="/freelancer/apply" testid="mnav-freelancer-apply" onClick={close}>Design with us</NavItem>
                {(!user || user === false) && (
                  <NavItem to="/login" testid="mnav-login" onClick={close}>Sign in</NavItem>
                )}
              </nav>

              <div className="mt-6 pt-6 border-t hairline flex flex-col gap-6">
                <button
                  data-testid="mnav-theme-toggle"
                  onClick={toggleTheme}
                  className="flex items-center gap-3 text-left text-sm tracking-wide text-muted-foreground hover:text-foreground transition-colors duration-300 min-h-11"
                >
                  {theme === "dark" ? <Sun className="h-4 w-4" strokeWidth={1} /> : <Moon className="h-4 w-4" strokeWidth={1} />}
                  {theme === "dark" ? "Light mode" : "Dark mode"}
                </button>
                {user && user !== false && (
                  <button
                    data-testid="mnav-logout"
                    onClick={async () => { close(); await logout(); nav("/"); }}
                    className="flex items-center gap-3 text-left text-sm tracking-wide text-muted-foreground hover:text-foreground transition-colors duration-300 min-h-11"
                  >
                    <LogOut className="h-4 w-4" strokeWidth={1} />
                    Sign out
                  </button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

